import { strFromU8, unzipSync } from 'fflate';

/**
 * Local .docx text extraction (Wave 56). Google Vision cannot read
 * Word files, so docx quotes/invoices were filing by filename with no
 * prefill. A .docx is a zip whose word/document.xml holds the text —
 * unzip locally (fflate, pure JS, works on native + web), strip the
 * XML, and feed the result through the same receipt parser the OCR
 * paths use. Zero API calls, zero quota.
 */

function base64ToBytes(base64: string): Uint8Array {
  // Hermes and browsers both provide atob.
  // eslint-disable-next-line no-undef
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
};

export function isDocxLike(mime: string | null | undefined, name: string | null | undefined): boolean {
  return (
    (mime?.includes('officedocument.wordprocessingml') ?? false) ||
    /\.docx$/i.test(name ?? '')
  );
}

/** Returns the document's plain text, or null when the file isn't a
 *  readable docx (legacy .doc, corrupt zip, empty body). */
export function extractDocxText(base64: string): string | null {
  try {
    const files = unzipSync(base64ToBytes(base64));
    const doc = files['word/document.xml'];
    if (!doc) return null;
    const xml = strFromU8(doc);
    const text = xml
      // Explicit tabs/breaks inside runs become spacing.
      .replace(/<w:tab[^>]*\/>/g, '\t')
      .replace(/<w:br[^>]*\/>/g, '\n')
      // Paragraph and table-row ends become line breaks.
      .replace(/<\/w:p>/g, '\n')
      .replace(/<\/w:tr>/g, '\n')
      // Table cells separate with spaces so "desc  qty  $amt" rows
      // stay on one parseable line.
      .replace(/<\/w:tc>/g, '  ')
      .replace(/<[^>]+>/g, '')
      .replace(/&(amp|lt|gt|quot|apos);/g, (m) => ENTITIES[m] ?? m)
      .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
      .replace(/[ \t]{3,}/g, '  ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return text.length >= 20 ? text : null;
  } catch {
    return null;
  }
}
